import { useEffect, useMemo, useRef, useState } from "react";
import { getLanguage, persistLanguage } from "../lib/i18n";
import "../styles/landing.css";

type Language = "pl" | "en";
type ThemeName = "light" | "dark";
type PaletteName = "studio" | "warm" | "cool" | "graphite" | "lime";
type SceneName = "interior" | "chair" | "variants" | "dimensions" | "callouts" | "features";

type TypeItem = {
  n: string;
  name: string;
  desc: string;
  pal: PaletteName;
  scene: SceneName;
  img?: string;
};

type Copy = {
  badge: string;
  h1Lines: string[];
  h1Highlights: string[];
  lead: string;
  cta: string;
  examples: string;
  login: string;
  jobBeforeTag: string;
  jobDimensionsTag: string;
  jobSource: string;
  beforeTag: string;
  afterTag: string;
  beforeCaption: string;
  afterCaption: string;
  problemEyebrow: string;
  problemTitle: string;
  problemBody: string;
  problemBody3: string;
  howEyebrow: string;
  howTitle: string;
  howDesc: string;
  stepPrefix: string;
  uploadMini: string;
  sampleDataFile: string;
  productRows: Array<[string, string, string]>;
  steps: Array<{ n: string; h: string; p: string }>;
  demoTitle: string;
  demoDesc: string;
  demoSteps: {
    upload: { title: string; subtitle: string };
    product: { title: string; subtitle: string };
    modes: { title: string; subtitle: string };
    results: { title: string; subtitle: string };
  };
  demoDone: string;
  demoDrop: string;
  demoAnalyze: string;
  demoChoose: string;
  demoGenerate: string;
  demoOpen: string;
  demoModeVariants: string;
  demoModeFeatures: string;
  typesEyebrow: string;
  typesTitle: string;
  typesDesc: string;
  types: TypeItem[];
  ctaH: string;
  ctaEm: string;
  ctaBtn: string;
  ctaGhost: string;
  footTag: string;
  footLinks: string[];
};

const ASSET = "/landing/";
const APP_PATH = "/app";

const COPY: Record<Language, Copy> = {
  pl: {
    badge: "v1.0 · Generator grafik produktowych",
    h1Lines: ["Twórz grafiki", "produktowe"],
    h1Highlights: ["z danych i", "zdjęć."],
    lead: "AvantoAI zamienia proste zdjęcia i dane produktu w profesjonalne karty produktu, grafiki marketplace'owe i materiały sprzedażowe.",
    cta: "Wygeneruj grafiki produktu",
    examples: "Zobacz przykłady",
    login: "Login",
    jobBeforeTag: "PRZED",
    jobDimensionsTag: "WYMIARY",
    jobSource: "chair_nord_raw.jpg + produkt.xlsx",
    beforeTag: "PRZED",
    afterTag: "PO",
    beforeCaption: "PRZED · surowe zdjęcie z fabryki",
    afterCaption: "PO · gotowa grafika sprzedażowa",
    problemEyebrow: "Problem",
    problemTitle: "Masz dane, ale brakuje gotowych grafik sprzedażowych?",
    problemBody: "Przy każdym nowym produkcie trzeba przygotować packshoty, aranżacje, warianty, wymiary, cechy i grafiki wyjaśniające produkt.",
    problemBody3: "To zabiera czas i opóźnia publikację produktu.",
    howEyebrow: "Rozwiązanie",
    howTitle: "AvantoAI tworzy komplet grafik z materiałów, które już masz.",
    howDesc: "Wgrywasz dane produktu, zdjęcia lub pliki źródłowe. Aplikacja analizuje informacje, tworzy edytowalny opis produktu i generuje grafiki sprzedażowe.",
    stepPrefix: "KROK",
    uploadMini: "WGRAJ XLSX + ZDJĘCIA",
    sampleDataFile: "produkt_NORD.xlsx",
    productRows: [
      ["Nazwa", "NORD · Fotel obrotowy", "99%"],
      ["Typ", "Fotel biurowy", "96%"],
      ["Wymiary", "62 × 60 × 105 cm", "92%"],
    ],
    steps: [
      { n: "01", h: "Wgraj materiały produktu", p: "Zdjęcia, opisy, tabele, dokumenty lub inne pliki z informacjami o produkcie." },
      { n: "02", h: "Sprawdź opis produktu", p: "Dane procesują się same: nazwa, warianty, wymiary, materiały i certyfikaty gotowe do weryfikacji." },
      { n: "03", h: "Wygeneruj grafiki", p: "Otrzymujesz zestaw materiałów do sklepu, marketplace'u albo oferty sprzedażowej." },
    ],
    demoTitle: "Jak wygląda aplikacja",
    demoDesc: "Cztery kroki od pliku do gotowych grafik.",
    demoSteps: {
      upload: { title: "Wgraj dane produktu", subtitle: "Excel + zdjęcia" },
      product: { title: "Sprawdź kartę produktu", subtitle: "AI wyciągnęło dane z Excela" },
      modes: { title: "Wybierz typy grafik", subtitle: "Staging, packshot, warianty" },
      results: { title: "Gotowe wyniki", subtitle: "Pobierz ZIP z grafikami" },
    },
    demoDone: "gotowe",
    demoDrop: "PRZECIĄGNIJ XLSX + ZDJĘCIA",
    demoAnalyze: "Analizuj produkt",
    demoChoose: "Wybierz grafiki",
    demoGenerate: "Generuj 3 grafiki",
    demoOpen: "Otwórz aplikację",
    demoModeVariants: "Warianty",
    demoModeFeatures: "Funkcje",
    typesEyebrow: "Co możesz wygenerować",
    typesTitle: "Jedno źródło produktu. Wiele grafik sprzedażowych.",
    typesDesc: "AvantoAI może wygenerować każdy z tych typów z tych samych danych produktu.",
    types: [
      { n: "01", name: "Staging we wnętrzu", desc: "Produkt w realistycznym wnętrzu: salon, biuro, loft.", pal: "warm", scene: "interior", img: `${ASSET}po4.png` },
      { n: "02", name: "Packshot studyjny", desc: "Białe tło, miękkie cienie, proporcje katalogowe.", pal: "studio", scene: "chair", img: `${ASSET}variant_tkanina_black.png` },
      { n: "03", name: "Aranżacje w różnych stylach", desc: "Kilka wariantów scenografii w spójnym tonie.", pal: "cool", scene: "interior" },
      { n: "04", name: "Lifestyle z użytkownikiem", desc: "Produkt w użyciu, z osobą i prawdziwym kontekstem.", pal: "warm", scene: "callouts", img: `${ASSET}po6.png` },
      { n: "05", name: "Plansza wariantów", desc: "Komplet wariantów kolorystycznych i materiałowych.", pal: "cool", scene: "variants" },
      { n: "06", name: "Diagram wymiarów", desc: "Rzut techniczny z wymiarami w cm, gotowy do specyfikacji.", pal: "studio", scene: "dimensions", img: `${ASSET}po2-5d09c962.png` },
      { n: "07", name: "Callouty detali", desc: "Zbliżenia: szwy, mechanizmy, materiały, wykończenia z opisami.", pal: "warm", scene: "callouts" },
      { n: "08", name: "Plansza funkcji", desc: "Najważniejsze cechy ułożone w sprzedażową kompozycję.", pal: "lime", scene: "features" },
      { n: "09", name: "Gwarancje i certyfikaty", desc: "Plansza zaufania: certyfikaty, gwarancje i normy.", pal: "graphite", scene: "features", img: `${ASSET}po3-c737bbaa.png` },
      { n: "10", name: "FAQ produktowe", desc: "Najczęstsze pytania w formacie graficznym.", pal: "studio", scene: "dimensions" },
      { n: "11", name: "Problem i rozwiązanie", desc: "Storyboard: problem klienta i sposób, w jaki produkt go rozwiązuje.", pal: "warm", scene: "interior" },
    ],
    ctaH: "Przygotuj profesjonalne grafiki produktowe",
    ctaEm: " samodzielnie.",
    ctaBtn: "Rozpocznij generowanie grafik",
    ctaGhost: "Porozmawiaj z nami",
    footTag: "AvantoAI · Grafiki produktowe dla mebli",
    footLinks: ["Polityka prywatności", "Regulamin", "Kontakt", "Status"],
  },
  en: {
    badge: "v1.0 · AI product visual generator",
    h1Lines: ["Create product", "graphics"],
    h1Highlights: ["from data", "and photos."],
    lead: "AvantoAI turns product data, photos and source files into ready-to-use visuals for product pages, marketplaces and sales materials.",
    cta: "Generate product visuals",
    examples: "See examples",
    login: "Log in",
    jobBeforeTag: "BEFORE",
    jobDimensionsTag: "DIMENSIONS",
    jobSource: "chair_nord_raw.jpg + product.xlsx",
    beforeTag: "BEFORE",
    afterTag: "AFTER",
    beforeCaption: "BEFORE · raw factory photo",
    afterCaption: "AFTER · ready-to-use sales visual",
    problemEyebrow: "Problem",
    problemTitle: "You have the data, but not the sales visuals",
    problemBody: "Every new product needs packshots, room scenes, variants, dimension diagrams, feature boards and explanatory visuals.",
    problemBody3: "Preparing them manually takes time and delays product launches.",
    howEyebrow: "Solution",
    howTitle: "AvantoAI creates a complete visual set from the materials you already have.",
    howDesc: "Upload product data, photos or source files. The app analyzes the information, creates an editable product description and generates sales visuals.",
    stepPrefix: "STEP",
    uploadMini: "UPLOAD XLSX + PHOTOS",
    sampleDataFile: "product_NORD.xlsx",
    productRows: [
      ["Name", "NORD · Office chair", "99%"],
      ["Type", "Office chair", "96%"],
      ["Dimensions", "62 × 60 × 105 cm", "92%"],
    ],
    steps: [
      { n: "01", h: "Upload product materials", p: "Photos, descriptions, spreadsheets, documents or any other files with product information." },
      { n: "02", h: "Review product description", p: "AvantoAI organizes name, variants, dimensions, materials, features and certifications for review." },
      { n: "03", h: "Generate visuals", p: "You receive a visual set ready for your store, marketplace listing or sales proposal." },
    ],
    demoTitle: "Inside the app",
    demoDesc: "Four steps from product files to finished visuals.",
    demoSteps: {
      upload: { title: "Upload product data", subtitle: "Spreadsheet + photos" },
      product: { title: "Review the product card", subtitle: "AI extracted the product data" },
      modes: { title: "Choose visual types", subtitle: "Staging, packshot, variants" },
      results: { title: "Finished outputs", subtitle: "Download the visuals as a ZIP" },
    },
    demoDone: "done",
    demoDrop: "DROP XLSX + PHOTOS",
    demoAnalyze: "Analyze product",
    demoChoose: "Choose visuals",
    demoGenerate: "Generate 3 visuals",
    demoOpen: "Open the app",
    demoModeVariants: "Variants",
    demoModeFeatures: "Features",
    typesEyebrow: "What you can generate",
    typesTitle: "One product source. Many sales visuals.",
    typesDesc: "AvantoAI can generate each of these types from the same product data.",
    types: [
      { n: "01", name: "Room staging", desc: "Product in a realistic interior: living room, office, loft.", pal: "warm", scene: "interior", img: `${ASSET}po4.png` },
      { n: "02", name: "Studio packshot", desc: "White background, soft shadows, catalog proportions.", pal: "studio", scene: "chair", img: `${ASSET}variant_tkanina_black.png` },
      { n: "03", name: "Style arrangements", desc: "Multiple scene variants in a coherent tone.", pal: "cool", scene: "interior" },
      { n: "04", name: "Lifestyle with person", desc: "Product in use with a person and a realistic context.", pal: "warm", scene: "callouts", img: `${ASSET}po6.png` },
      { n: "05", name: "Variants board", desc: "All colorways and material options in one graphic.", pal: "cool", scene: "variants" },
      { n: "06", name: "Dimension diagram", desc: "Technical view with measurements in cm, ready for specs.", pal: "studio", scene: "dimensions", img: `${ASSET}po2-5d09c962.png` },
      { n: "07", name: "Detail callouts", desc: "Close-ups of stitching, mechanisms, materials and finishes with labels.", pal: "warm", scene: "callouts" },
      { n: "08", name: "Feature board", desc: "Key features arranged into a sales composition.", pal: "lime", scene: "features" },
      { n: "09", name: "Trust & certifications", desc: "Trust board: certifications, warranties and safety standards.", pal: "graphite", scene: "features", img: `${ASSET}po3-c737bbaa.png` },
      { n: "10", name: "Product FAQ", desc: "Common questions in a graphic format.", pal: "studio", scene: "dimensions" },
      { n: "11", name: "Problem & solution", desc: "Storyboard: a customer issue and how the product solves it.", pal: "warm", scene: "interior" },
    ],
    ctaH: "Prepare professional product visuals",
    ctaEm: " on your own.",
    ctaBtn: "Start generating visuals",
    ctaGhost: "Talk to us",
    footTag: "AvantoAI · Product visuals for furniture brands",
    footLinks: ["Privacy", "Terms", "Contact", "Status"],
  },
};

const PALETTES: Record<PaletteName, { bg: string; fg: string; acc: string; floor: string }> = {
  studio: { bg: "#f4f3ee", fg: "#1a1a18", acc: "oklch(0.86 0.18 130)", floor: "#e6e4dc" },
  warm: { bg: "#e8ddc8", fg: "#3a2818", acc: "#c87a4a", floor: "#cfb798" },
  cool: { bg: "#dde4ea", fg: "#1d2933", acc: "#7896aa", floor: "#c2cdd5" },
  graphite: { bg: "#2a2a26", fg: "#e8e6df", acc: "oklch(0.86 0.18 130)", floor: "#1d1d1a" },
  lime: { bg: "oklch(0.94 0.08 130)", fg: "#1a2010", acc: "oklch(0.66 0.18 130)", floor: "oklch(0.88 0.10 130)" },
};

function Scene({ scene, palette }: { scene: SceneName; palette: PaletteName }) {
  const p = PALETTES[palette];

  if (scene === "interior") {
    return (
      <>
        <rect x="0" y="0" width="100" height="62" fill={p.floor} opacity=".4" />
        <rect x="0" y="62" width="100" height="38" fill={p.floor} />
        <rect x="35" y="38" width="40" height="28" rx="2" fill={p.acc} opacity=".85" />
        <rect x="38" y="62" width="3" height="6" fill={p.fg} opacity=".4" />
        <rect x="69" y="62" width="3" height="6" fill={p.fg} opacity=".4" />
        <rect x="8" y="22" width="22" height="32" fill={p.fg} opacity=".07" />
        <rect x="78" y="44" width="14" height="22" fill={p.fg} opacity=".08" />
      </>
    );
  }

  if (scene === "chair") {
    return (
      <>
        <rect x="0" y="68" width="100" height="32" fill={p.floor} />
        <ellipse cx="50" cy="86" rx="22" ry="3" fill={p.fg} opacity=".1" />
        <rect x="36" y="32" width="28" height="36" rx="3" fill={p.acc} />
        <rect x="36" y="32" width="28" height="6" rx="3" fill={p.fg} opacity=".1" />
        <rect x="38" y="68" width="3" height="14" fill={p.fg} opacity=".5" />
        <rect x="59" y="68" width="3" height="14" fill={p.fg} opacity=".5" />
      </>
    );
  }

  if (scene === "variants") {
    return (
      <>
        {[0, 1, 2].map((index) => (
          <g key={index} transform={`translate(${10 + index * 30},25)`}>
            <rect width="22" height="38" rx="2" fill={index === 0 ? p.acc : index === 1 ? p.fg : "#999"} opacity={index === 0 ? 1 : 0.8} />
            <text x="11" y="50" fontFamily="monospace" fontSize="3.5" fill={p.fg} opacity=".55" textAnchor="middle">
              VAR {String.fromCharCode(65 + index)}
            </text>
          </g>
        ))}
      </>
    );
  }

  if (scene === "dimensions") {
    return (
      <>
        <rect x="30" y="28" width="40" height="42" rx="2" fill={p.acc} opacity=".5" />
        <line x1="20" y1="28" x2="20" y2="70" stroke={p.fg} strokeWidth=".3" />
        <line x1="18" y1="28" x2="22" y2="28" stroke={p.fg} strokeWidth=".3" />
        <line x1="18" y1="70" x2="22" y2="70" stroke={p.fg} strokeWidth=".3" />
        <text x="14" y="52" fontFamily="monospace" fontSize="4" fill={p.fg} opacity=".7">
          82
        </text>
        <line x1="30" y1="78" x2="70" y2="78" stroke={p.fg} strokeWidth=".3" />
        <text x="46" y="86" fontFamily="monospace" fontSize="4" fill={p.fg} opacity=".7">
          62cm
        </text>
      </>
    );
  }

  if (scene === "callouts") {
    return (
      <>
        <rect x="20" y="26" width="60" height="48" rx="3" fill={p.acc} opacity=".6" />
        <circle cx="32" cy="38" r="3" fill="none" stroke={p.fg} strokeWidth=".5" />
        <line x1="35" y1="38" x2="48" y2="20" stroke={p.fg} strokeWidth=".4" />
        <rect x="48" y="14" width="22" height="8" rx="1" fill={p.bg} stroke={p.fg} strokeWidth=".4" />
        <text x="59" y="20" fontFamily="monospace" fontSize="3.5" fill={p.fg} textAnchor="middle">
          FOAM
        </text>
        <circle cx="68" cy="60" r="3" fill="none" stroke={p.fg} strokeWidth=".5" />
        <line x1="68" y1="63" x2="80" y2="80" stroke={p.fg} strokeWidth=".4" />
        <rect x="70" y="80" width="22" height="8" rx="1" fill={p.bg} stroke={p.fg} strokeWidth=".4" />
        <text x="81" y="86" fontFamily="monospace" fontSize="3.5" fill={p.fg} textAnchor="middle">
          BASE
        </text>
      </>
    );
  }

  return (
    <>
      <rect x="6" y="10" width="40" height="28" rx="2" fill={p.acc} opacity=".7" />
      <text x="26" y="26" fontFamily="monospace" fontSize="4" fill={p.fg} textAnchor="middle">
        120kg
      </text>
      <text x="26" y="32" fontFamily="monospace" fontSize="2.8" fill={p.fg} opacity=".6" textAnchor="middle">
        LOAD
      </text>
      <rect x="54" y="10" width="40" height="28" rx="2" fill={p.fg} opacity=".1" />
      <text x="74" y="26" fontFamily="monospace" fontSize="4" fill={p.fg} textAnchor="middle">
        8h+
      </text>
      <text x="74" y="32" fontFamily="monospace" fontSize="2.8" fill={p.fg} opacity=".6" textAnchor="middle">
        COMFORT
      </text>
      <rect x="6" y="42" width="40" height="28" rx="2" fill={p.fg} opacity=".1" />
      <text x="26" y="58" fontFamily="monospace" fontSize="4" fill={p.fg} textAnchor="middle">
        360
      </text>
      <rect x="54" y="42" width="40" height="28" rx="2" fill={p.acc} opacity=".7" />
      <text x="74" y="58" fontFamily="monospace" fontSize="4" fill={p.fg} textAnchor="middle">
        5yr
      </text>
    </>
  );
}

function VisualTile({ palette = "studio", scene = "chair", tag }: { palette?: PaletteName; scene?: SceneName; tag?: string }) {
  const p = PALETTES[palette];

  return (
    <div className="landing-ph" style={{ background: p.bg }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
        <Scene scene={scene} palette={palette} />
      </svg>
      {tag ? <span>{tag}</span> : null}
    </div>
  );
}

function Logo({ small = false }: { small?: boolean }) {
  return (
    <div className={small ? "landing-logo landing-logo-sm" : "landing-logo"}>
      <span className="landing-logo-mark" />
      AvantoAI
    </div>
  );
}

function Nav({ lang, setLang, theme, setTheme, t }: { lang: Language; setLang: (value: Language) => void; theme: ThemeName; setTheme: (value: ThemeName) => void; t: Copy }) {
  return (
    <nav className="landing-nav">
      <div className="landing-wrap landing-nav-inner">
        <Logo />
        <div className="landing-nav-actions">
          <button className="landing-theme-btn" type="button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Toggle theme">
            <span className="landing-theme-knob" />
          </button>
          <div className="landing-lang" aria-label="Language">
            <button className={lang === "pl" ? "is-on" : ""} type="button" onClick={() => setLang("pl")}>
              PL
            </button>
            <button className={lang === "en" ? "is-on" : ""} type="button" onClick={() => setLang("en")}>
              EN
            </button>
          </div>
          <a href="/users/sign_in" className="landing-btn landing-btn-ghost">
            {t.login}
          </a>
          <a href={APP_PATH} target="_top" className="landing-btn landing-btn-dark">
            {t.cta}
          </a>
        </div>
      </div>
    </nav>
  );
}

function JobBoard({ t }: { t: Copy }) {
  const tiles = [
    { src: `${ASSET}przed1.JPG`, tag: t.jobBeforeTag, wide: true },
    { src: `${ASSET}variant_tkanina_black.png`, tag: "PACKSHOT" },
    { src: `${ASSET}po4.png`, tag: "STAGING" },
    { src: `${ASSET}po2-5d09c962.png`, tag: t.jobDimensionsTag },
    { src: `${ASSET}po6.png`, tag: "LIFESTYLE" },
  ];

  return (
    <div className="landing-job-board" aria-hidden="true">
      <div className="landing-jb-head">
        <span>
          <i />
          job_8af3c2 · NORD chair
        </span>
        <span>generating · 5 / 6</span>
      </div>
      <div className="landing-jb-grid">
        {tiles.map((tile) => (
          <div key={tile.tag} className={tile.wide ? "landing-jb-tile is-wide" : "landing-jb-tile"}>
            <img src={tile.src} alt="" />
            <span>{tile.tag}</span>
          </div>
        ))}
      </div>
      <div className="landing-jb-foot">
        <span>{t.jobSource}</span>
        <span>~84s</span>
      </div>
    </div>
  );
}

function Hero({ t }: { t: Copy }) {
  return (
    <section className="landing-hero">
      <div className="landing-hero-media">
        <JobBoard t={t} />
      </div>
      <div className="landing-wrap landing-hero-content">
        <div className="landing-badge">
          <i />
          <span>{t.badge}</span>
        </div>
        <h1>
          {t.h1Lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
          {t.h1Highlights.map((line) => (
            <em key={line}>{line}</em>
          ))}
        </h1>
        <p>{t.lead}</p>
        <div className="landing-hero-actions">
          <a href={APP_PATH} target="_top" className="landing-btn landing-btn-lg landing-btn-dark">
            {t.cta}
          </a>
          <a href="#types" className="landing-btn landing-btn-lg landing-btn-light">
            {t.examples}
          </a>
        </div>
      </div>
    </section>
  );
}

function BeforeAfter({ t }: { t: Copy }) {
  const [pos, setPos] = useState(52);
  const ref = useRef<HTMLDivElement | null>(null);

  const updatePosition = (clientX: number) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const next = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setPos(Math.round(next * 100));
  };

  return (
    <div
      ref={ref}
      className="landing-before-after"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        updatePosition(event.clientX);
      }}
      onPointerMove={(event) => {
        if (event.buttons === 1) updatePosition(event.clientX);
      }}
      role="img"
      aria-label="Before and after product graphic comparison"
    >
      <img src={`${ASSET}variant_tkanina_black.png`} alt="" />
      <div style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={`${ASSET}przed1.JPG`} alt="" />
      </div>
      <span className="landing-ba-line" style={{ left: `${pos}%` }} />
      <span className="landing-ba-handle" style={{ left: `${pos}%` }}>
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M7 4 3 10l4 6M13 4l4 6-4 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <b className="landing-ba-left">{t.beforeTag}</b>
      <b className="landing-ba-right">{t.afterTag}</b>
    </div>
  );
}

function Problem({ t }: { t: Copy }) {
  return (
    <section className="landing-section landing-problem">
      <div className="landing-wrap landing-problem-grid">
        <div>
          <Eyebrow>{t.problemEyebrow}</Eyebrow>
          <h2>{t.problemTitle}</h2>
          <p>{t.problemBody}</p>
          <strong>{t.problemBody3}</strong>
        </div>
        <div>
          <BeforeAfter t={t} />
          <div className="landing-ba-caption">
            <span>{t.beforeCaption}</span>
            <span>{t.afterCaption}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Eyebrow({ children }: { children: string }) {
  return (
    <div className="landing-eyebrow">
      <i />
      {children}
    </div>
  );
}

function HowVisual({ index, t }: { index: number; t: Copy }) {
  if (index === 0) {
    return (
      <div className="landing-how-vis landing-upload-vis">
        <div className="landing-drop-mini">{t.uploadMini}</div>
        <FileLine type="xlsx" name={t.sampleDataFile} size="4.2 MB" />
        <FileLine type="img" name="nord_01.jpg" size="3.8 MB" />
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="landing-how-vis landing-product-vis">
        {t.productRows.map(([label, value, confidence]) => (
          <div key={label}>
            <span>{label}</span>
            <b>{value}</b>
            <em>{confidence}</em>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="landing-how-vis landing-result-vis">
      {[`${ASSET}po3-c737bbaa.png`, `${ASSET}po4.png`, `${ASSET}po2-5d09c962.png`, `${ASSET}po6.png`, `${ASSET}variant_tkanina_black.png`].map((src) => (
        <img key={src} src={src} alt="" />
      ))}
    </div>
  );
}

function FileLine({ type, name, size }: { type: string; name: string; size: string }) {
  return (
    <div className="landing-file-line">
      <span className={type === "xlsx" ? "is-xlsx" : ""}>{type.toUpperCase()}</span>
      <b>{name}</b>
      <em>{size}</em>
    </div>
  );
}

function How({ t }: { t: Copy }) {
  return (
    <section className="landing-section">
      <div className="landing-wrap">
        <Eyebrow>{t.howEyebrow}</Eyebrow>
        <h2 className="landing-section-title">{t.howTitle}</h2>
        <p className="landing-section-lead">{t.howDesc}</p>
        <div className="landing-how-grid">
          {t.steps.map((step, index) => (
            <article key={step.n}>
              <span>{t.stepPrefix} {step.n}</span>
              <h3>{step.h}</h3>
              <p>{step.p}</p>
              <HowVisual index={index} t={t} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Demo({ t }: { t: Copy }) {
  const [step, setStep] = useState<"upload" | "product" | "modes" | "results">("upload");
  const order = useMemo(() => ["upload", "product", "modes", "results"] as const, []);
  const index = order.indexOf(step);

  return (
    <section className="landing-section landing-demo-section">
      <div className="landing-wrap">
        <Eyebrow>{t.demoTitle}</Eyebrow>
        <h2 className="landing-section-title">{t.demoTitle}</h2>
        <p className="landing-section-lead">{t.demoDesc}</p>
        <div className="landing-demo">
          <div className="landing-demo-bar">
            <div className="landing-traffic">
              <i />
              <i />
              <i />
            </div>
            <div className="landing-demo-tabs">
              {order.map((value, tabIndex) => (
                <button key={value} type="button" className={step === value ? "is-active" : index > tabIndex ? "is-done" : ""} onClick={() => setStep(value)}>
                  {tabIndex + 1 < 10 ? `0${tabIndex + 1}` : tabIndex + 1} · {value}
                </button>
              ))}
            </div>
            <span>avantoai.ai/app</span>
          </div>
          <div className="landing-demo-content">
            <DemoStep step="upload" active={step === "upload"} done={index > 0} setStep={setStep} title={t.demoSteps.upload.title} subtitle={t.demoSteps.upload.subtitle} t={t} />
            <DemoStep step="product" active={step === "product"} done={index > 1} locked={index < 1} setStep={setStep} title={t.demoSteps.product.title} subtitle={t.demoSteps.product.subtitle} t={t} />
            <DemoStep step="modes" active={step === "modes"} done={index > 2} locked={index < 2} setStep={setStep} title={t.demoSteps.modes.title} subtitle={t.demoSteps.modes.subtitle} t={t} />
            <DemoStep step="results" active={step === "results"} locked={index < 3} setStep={setStep} title={t.demoSteps.results.title} subtitle={t.demoSteps.results.subtitle} t={t} />
          </div>
        </div>
      </div>
    </section>
  );
}

function DemoStep({
  step,
  active,
  done = false,
  locked = false,
  title,
  subtitle,
  setStep,
  t,
}: {
  step: "upload" | "product" | "modes" | "results";
  active: boolean;
  done?: boolean;
  locked?: boolean;
  title: string;
  subtitle: string;
  setStep: (value: "upload" | "product" | "modes" | "results") => void;
  t: Copy;
}) {
  if (locked) {
    return (
      <div className="landing-demo-row is-locked">
        <span>{step}</span>
        <b>{title}</b>
      </div>
    );
  }

  if (!active) {
    return (
      <button type="button" className="landing-demo-row" onClick={() => setStep(step)}>
        <span>{step}</span>
        <b>{title}</b>
        {done ? <em>{t.demoDone}</em> : null}
      </button>
    );
  }

  return (
    <div className="landing-demo-open">
      <div className="landing-demo-open-head">
        <span>{step}</span>
        <div>
          <b>{title}</b>
          <p>{subtitle}</p>
        </div>
      </div>
      <DemoPayload step={step} setStep={setStep} t={t} />
    </div>
  );
}

function DemoPayload({ step, setStep, t }: { step: "upload" | "product" | "modes" | "results"; setStep: (value: "upload" | "product" | "modes" | "results") => void; t: Copy }) {
  if (step === "upload") {
    return (
      <div className="landing-demo-payload">
        <div className="landing-drop-large">{t.demoDrop}</div>
        <button type="button" onClick={() => setStep("product")}>
          {t.demoAnalyze}
        </button>
      </div>
    );
  }

  if (step === "product") {
    return (
      <div className="landing-demo-payload">
        <div className="landing-product-vis is-demo">
          {t.productRows.map(([label, value, confidence]) => (
            <div key={label}>
              <span>{label}</span>
              <b>{value}</b>
              <em>{confidence}</em>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setStep("modes")}>
          {t.demoChoose}
        </button>
      </div>
    );
  }

  if (step === "modes") {
    return (
      <div className="landing-demo-payload">
        <div className="landing-mode-mini-grid">
          {[
            ["warm", "interior", "Staging", true],
            ["studio", "chair", "Packshot", true],
            ["cool", "variants", t.demoModeVariants, true],
            ["lime", "features", t.demoModeFeatures, false],
          ].map(([palette, scene, name, selected]) => (
            <div key={String(name)} className={selected ? "is-selected" : ""}>
              <VisualTile palette={palette as PaletteName} scene={scene as SceneName} />
              <span>{name}</span>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setStep("results")}>
          {t.demoGenerate}
        </button>
      </div>
    );
  }

  return (
    <div className="landing-demo-payload">
      <div className="landing-result-mini-grid">
        {[
          [`${ASSET}po4.png`, "STAGING"],
          [`${ASSET}variant_tkanina_black.png`, "PACKSHOT"],
          [`${ASSET}po2-5d09c962.png`, t.jobDimensionsTag],
        ].map(([src, tag]) => (
          <div key={tag}>
            <img src={src} alt="" />
            <span>{tag}</span>
          </div>
        ))}
      </div>
      <a href={APP_PATH} target="_top">{t.demoOpen}</a>
    </div>
  );
}

function Types({ t }: { t: Copy }) {
  const [active, setActive] = useState(0);
  const current = t.types[active] ?? t.types[0];

  if (!current) return null;

  return (
    <section className="landing-section" id="types">
      <div className="landing-wrap">
        <Eyebrow>{t.typesEyebrow}</Eyebrow>
        <h2 className="landing-section-title">{t.typesTitle}</h2>
        <p className="landing-section-lead">{t.typesDesc}</p>
        <div className="landing-types">
          <div className="landing-type-list">
            {t.types.map((item, index) => (
              <button key={item.n} type="button" className={active === index ? "is-active" : ""} onClick={() => setActive(index)}>
                <span>{item.n}</span>
                <b>{item.name}</b>
                <em>{item.desc}</em>
              </button>
            ))}
          </div>
          <div className="landing-type-preview">
            <div>
              {current.img ? <img src={current.img} alt={current.name} /> : <VisualTile palette={current.pal} scene={current.scene} />}
            </div>
            <footer>
              <span>TYPE {current.n}</span>
              <b>{current.name}</b>
              <p>{current.desc}</p>
            </footer>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ t }: { t: Copy }) {
  return (
    <section className="landing-cta">
      <div className="landing-wrap">
        <h2>
          {t.ctaH}
          <em>{t.ctaEm}</em>
        </h2>
        <div>
          <a href={APP_PATH} target="_top" className="landing-btn landing-btn-lg landing-btn-dark">
            {t.ctaBtn}
          </a>
          <a href="mailto:hello@avantoai.ai" className="landing-btn landing-btn-lg landing-btn-light">
            {t.ctaGhost}
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer({ t }: { t: Copy }) {
  return (
    <footer className="landing-footer">
      <div className="landing-wrap">
        <Logo small />
        <span>{t.footTag}</span>
        <div>
          {t.footLinks.map((link) => (
            <a key={link} href="#">
              {link}
            </a>
          ))}
        </div>
        <em>© 2026 · v2</em>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [lang, setLang] = useState<Language>(() => getLanguage());
  const [theme, setTheme] = useState<ThemeName>("light");
  const t = COPY[lang];

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    persistLanguage(lang);
  }, [lang]);

  return (
    <div className="landing-page">
      <Nav lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} t={t} />
      <main>
        <Hero t={t} />
        <Problem t={t} />
        <How t={t} />
        <Demo t={t} />
        <Types t={t} />
        <FinalCTA t={t} />
      </main>
      <Footer t={t} />
    </div>
  );
}
